import React, { useRef, useEffect, useCallback } from 'react';
import { useConsoleStore } from '../../store/consoleStore';
import { ConsoleMessage } from './ConsoleMessage';

interface Props {
  onNavigateToCode?: (fileName: string, lineNumber: number, columnNumber: number) => void;
}

export const ConsoleMessageList: React.FC<Props> = ({ onNavigateToCode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const filteredMessages = useConsoleStore((state) => state.filteredMessages);
  const isScrolledToBottom = useConsoleStore((state) => state.isScrolledToBottom);
  const setScrolledToBottom = useConsoleStore((state) => state.setScrolledToBottom);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isScrolledToBottom && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [filteredMessages, isScrolledToBottom]);

  // Handle scroll to detect if user scrolled away from bottom
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setScrolledToBottom(isAtBottom);
    }
  }, [setScrolledToBottom]);

  // Scroll to bottom button click
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      {/* Message list */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden"
      >
        {filteredMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
            No messages to display
          </div>
        ) : (
          filteredMessages.map((message) => (
            <ConsoleMessage
              key={message.id}
              message={message}
              onNavigateToCode={onNavigateToCode}
            />
          ))
        )}
      </div>

      {/* Scroll to bottom button (visible when not at bottom) */}
      {!isScrolledToBottom && filteredMessages.length > 0 && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-2 right-4 px-3 py-1.5 bg-blue-600 hover:bg-blue-500
            text-white text-xs rounded-full shadow-lg transition-colors"
        >
          Scroll to bottom
        </button>
      )}
    </div>
  );
};
